import { EtiquetaDeRisco } from '../../../components/Etiqueta'
import { Cartao, ListaDeDados, type Dado } from '../../../components/ListaDeDados'
import { dataHora } from '../../../lib/datas'
import { TERMOS } from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'

/**
 * A avaliação de risco e a janela em que a mudança vai (ou foi) para produção.
 *
 * Probabilidade e impacto só aparecem quando alguém avaliou. São nulos até lá — nunca zero — e
 * imprimir "0 de 5" faria a tela afirmar uma avaliação de risco mínimo que ninguém fez, que é o
 * mesmo erro de fundo do histórico importado passar por aprovado.
 *
 * "Fora da janela" só aparece quando é verdade, e nunca sozinho: o schema exige a justificativa
 * junto (V4), e é ela que a fiscalização lê. Um "sim" sem o porquê seria a marca da irregularidade
 * sem a defesa dela.
 *
 * @param props a solicitação
 * @returns o cartão de risco e janela
 */
export function PainelDeRisco({ gmud }: { gmud: DetalheGmud }) {
  const dados: Dado[] = [
    { rotulo: TERMOS.risco.rotulo, valor: <EtiquetaDeRisco risco={gmud.riscoNivel} /> },
    ...(gmud.riscoProbabilidade != null
      ? [{ rotulo: 'Probabilidade', valor: `${gmud.riscoProbabilidade} de 5` }]
      : []),
    ...(gmud.riscoImpacto != null ? [{ rotulo: 'Impacto', valor: `${gmud.riscoImpacto} de 5` }] : []),
    {
      rotulo: TERMOS.bloqueadoParaDeploy.rotulo,
      valor: gmud.bloqueadoParaDeploy ? 'Bloqueada para produção' : 'Liberada pelo comitê',
    },
    { rotulo: 'Implantação prevista', valor: dataHora(gmud.dataImplementacaoPrevista) },
    { rotulo: 'Implantação executada', valor: dataHora(gmud.dataImplementacaoExecutada) },
    {
      rotulo: 'Parada de serviço',
      valor: gmud.indisponibilidadePrevista
        ? `Prevista${gmud.indisponibilidadeDuracao ? ` — ${gmud.indisponibilidadeDuracao}` : ''}`
        : 'Não prevista',
    },
  ]

  return (
    <Cartao titulo="Risco e janela" descricao={TERMOS.risco.ajuda}>
      <div className="space-y-5">
        <ListaDeDados dados={dados} />

        {gmud.foraJanela && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold">Implantação fora da janela acordada</p>
            <p className="mt-1 max-w-prose whitespace-pre-line">
              {gmud.foraJanelaJustificativa || 'Sem justificativa registrada.'}
            </p>
          </div>
        )}

        <p className="max-w-prose text-xs text-slate-500">
          {TERMOS.janelaImplantacao.rotulo}: {TERMOS.janelaImplantacao.ajuda}
        </p>
      </div>
    </Cartao>
  )
}
